import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  CGK_TRADES,
  CGK_PRIORITIES,
  CGK_CUSTOMER_TYPES,
  CGK_TRADE_SERVICES,
  US_STATES,
  type CgkPriorityId,
  type CgkTrade,
  type CgkCustomerType,
} from '@maintenance-tracker/shared';
import { BrandLogo } from '@/components/brand/brand-logo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api, ApiError } from '@/lib/utils';
import { useAuth } from '@/lib/auth';
import { consumeNextPath } from '@/lib/deeplink';
import { cn } from '@/lib/utils';

type BusinessMe = {
  business: {
    id: string;
    name: string;
    trade: string | null;
    phone: string | null;
    websiteUrl: string | null;
    city: string | null;
    state: string | null;
    differentiators: string | null;
    yearsInBusiness: number | null;
    services: string[];
    onboardingComplete: boolean;
  } | null;
  onboarding: {
    step: number;
    status: string;
    priorities: CgkPriorityId[];
    noWebsite: boolean;
    customerType: CgkCustomerType | null;
    firstName: string;
    tradeOther: string;
    serviceAreaText: string;
  };
};

const STEPS = [
  'Your business',
  'What to improve',
  'Your website',
  'Where you work',
  'What you do',
  'First action',
];

export function OnboardingPage() {
  const navigate = useNavigate();
  const { refresh, user } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [businessName, setBusinessName] = useState('');
  const [trade, setTrade] = useState<CgkTrade | ''>('');
  const [tradeOther, setTradeOther] = useState('');
  const [phone, setPhone] = useState('');
  const [firstName, setFirstName] = useState('');
  const [priorities, setPriorities] = useState<CgkPriorityId[]>([]);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [noWebsite, setNoWebsite] = useState(false);
  const [stateCode, setStateCode] = useState('');
  const [city, setCity] = useState('');
  const [serviceAreaText, setServiceAreaText] = useState('');
  const [customerType, setCustomerType] = useState<CgkCustomerType | null>(null);
  const [services, setServices] = useState<string[]>([]);
  const [customService, setCustomService] = useState('');
  const [differentiator, setDifferentiator] = useState('');
  const [years, setYears] = useState('');

  useEffect(() => {
    void (async () => {
      try {
        const data = await api<BusinessMe>('/api/v1/business/me');
        if (data.business?.onboardingComplete || data.onboarding.status === 'complete') {
          navigate({ to: consumeNextPath() });
          return;
        }
        if (data.business) {
          setBusinessName(data.business.name === 'My business' ? '' : data.business.name);
          setPhone(data.business.phone ?? '');
          setWebsiteUrl(data.business.websiteUrl ?? '');
          setCity(data.business.city ?? '');
          setStateCode(data.business.state ?? '');
          setServices(data.business.services ?? []);
          setDifferentiator(data.business.differentiators ?? '');
          setYears(data.business.yearsInBusiness != null ? String(data.business.yearsInBusiness) : '');
          const t = data.business.trade;
          if (t && (CGK_TRADES as readonly string[]).includes(t)) setTrade(t as CgkTrade);
          else if (t) {
            setTrade('Other');
            setTradeOther(t);
          }
        }
        setPriorities(data.onboarding.priorities ?? []);
        setNoWebsite(data.onboarding.noWebsite);
        setCustomerType(data.onboarding.customerType);
        setFirstName(data.onboarding.firstName || user?.user?.name?.split(' ')[0] || '');
        setTradeOther((v) => data.onboarding.tradeOther || v);
        setServiceAreaText(data.onboarding.serviceAreaText || '');
        setStep(Math.min(6, Math.max(1, data.onboarding.step || 1)));
      } catch {
        /* new account */
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate, user?.user?.name]);

  const save = async (opts: { step: number; complete?: boolean; next?: number }) => {
    setError('');
    setSaving(true);
    try {
      await api('/api/v1/business/onboarding', {
        method: 'PATCH',
        body: JSON.stringify({
          step: opts.step,
          complete: opts.complete,
          business_name: businessName.trim() || undefined,
          trade: trade || null,
          trade_other: tradeOther,
          phone,
          first_name: firstName,
          priorities,
          website_url: noWebsite ? null : websiteUrl,
          no_website: noWebsite,
          state: stateCode || null,
          city: city || null,
          service_area_text: serviceAreaText,
          customer_type: customerType,
          services,
          differentiator: differentiator || null,
          years_in_business: years ? parseInt(years, 10) : null,
        }),
      });
      await refresh();
      if (opts.complete) {
        navigate({ to: consumeNextPath() });
        return;
      }
      if (opts.next) setStep(opts.next);
    } catch (e) {
      setError((e as ApiError).message || 'Could not save. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const togglePriority = (id: CgkPriorityId) => {
    setPriorities((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]));
  };

  const toggleService = (s: string) => {
    setServices((prev) => {
      if (prev.includes(s)) return prev.filter((x) => x !== s);
      if (prev.length >= 12) return prev;
      return [...prev, s];
    });
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center text-text-secondary">Loading…</div>
    );
  }

  const serviceSuggestions = trade ? CGK_TRADE_SERVICES[trade] ?? CGK_TRADE_SERVICES.Other : CGK_TRADE_SERVICES.Other;

  return (
    <div className="min-h-screen bg-bg-app">
      <header className="border-b border-border bg-bg-surface px-4 py-4">
        <div className="mx-auto flex max-w-lg items-center justify-between">
          <BrandLogo size="sm" to="/" />
          <span className="text-xs text-text-muted">
            Step {step} of {STEPS.length}
          </span>
        </div>
        <div className="mx-auto mt-4 max-w-lg">
          <div className="h-1.5 overflow-hidden rounded-full bg-bg-subtle">
            <div
              className="h-full rounded-full bg-brand transition-all"
              style={{ width: `${(step / STEPS.length) * 100}%` }}
            />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-lg px-4 py-8">
        <h1 className="text-2xl font-semibold tracking-tight text-text-primary">{STEPS[step - 1]}</h1>
        {error && <p className="mt-3 text-sm text-danger">{error}</p>}

        {step === 1 && (
          <div className="mt-6 space-y-4">
            <Input
              label="Business name"
              required
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
            />
            <div>
              <label className="mb-1.5 block text-[13px] font-medium text-text-primary">Trade</label>
              <select
                className="h-10 w-full rounded-lg border border-border-strong bg-bg-surface px-3 text-sm"
                value={trade}
                onChange={(e) => setTrade(e.target.value as CgkTrade)}
              >
                <option value="">Select…</option>
                {CGK_TRADES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
            {trade === 'Other' && (
              <Input label="Describe your trade" value={tradeOther} onChange={(e) => setTradeOther(e.target.value)} />
            )}
            <Input
              label="US phone"
              inputMode="tel"
              placeholder="5551234567"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              helper="10 digits"
            />
            <Input label="Your first name" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
            <Button
              className="w-full"
              size="lg"
              loading={saving}
              onClick={() => {
                if (!businessName.trim()) {
                  setError('Business name is required');
                  return;
                }
                void save({ step: 1, next: 2 });
              }}
            >
              Continue
            </Button>
          </div>
        )}

        {step === 2 && (
          <div className="mt-6 space-y-3">
            <p className="text-sm text-text-secondary">
              Pick what matters most. This personalizes Home — it never locks features.
            </p>
            {CGK_PRIORITIES.map((p) => (
              <label
                key={p.id}
                className={cn(
                  'flex cursor-pointer items-start gap-3 rounded-xl border p-3',
                  priorities.includes(p.id) ? 'border-brand bg-brand-subtle' : 'border-border bg-bg-surface',
                )}
              >
                <input
                  type="checkbox"
                  checked={priorities.includes(p.id)}
                  onChange={() => togglePriority(p.id)}
                  className="mt-1"
                />
                <span className="text-sm font-medium text-text-primary">{p.label}</span>
              </label>
            ))}
            <div className="flex gap-2 pt-2">
              <Button variant="secondary" className="flex-1" onClick={() => setStep(3)}>
                Skip
              </Button>
              <Button className="flex-1" loading={saving} onClick={() => void save({ step: 2, next: 3 })}>
                Continue
              </Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="mt-6 space-y-4">
            <p className="text-sm text-text-secondary">
              We use this to review your public pages. We never silently substitute another company.
            </p>
            {!noWebsite && (
              <Input
                label="Website URL"
                placeholder="yourwebsite.com"
                value={websiteUrl}
                onChange={(e) => setWebsiteUrl(e.target.value)}
              />
            )}
            <label className="flex items-center gap-2 text-sm text-text-secondary">
              <input
                type="checkbox"
                checked={noWebsite}
                onChange={(e) => setNoWebsite(e.target.checked)}
              />
              I don&apos;t have a website yet
            </label>
            <div className="flex gap-2">
              <Button variant="secondary" className="flex-1" onClick={() => setStep(4)}>
                Skip
              </Button>
              <Button className="flex-1" loading={saving} onClick={() => void save({ step: 3, next: 4 })}>
                Continue
              </Button>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="mt-6 space-y-4">
            <div>
              <label className="mb-1.5 block text-[13px] font-medium text-text-primary">State</label>
              <select
                className="h-10 w-full rounded-lg border border-border-strong bg-bg-surface px-3 text-sm"
                value={stateCode}
                onChange={(e) => setStateCode(e.target.value)}
              >
                <option value="">Select…</option>
                {US_STATES.map((s) => (
                  <option key={s.code} value={s.code}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
            <Input label="Primary city" value={city} onChange={(e) => setCity(e.target.value)} />
            <Input
              label="Service area"
              placeholder="Cities, counties, or radius"
              value={serviceAreaText}
              onChange={(e) => setServiceAreaText(e.target.value)}
            />
            <div className="flex flex-wrap gap-2">
              {CGK_CUSTOMER_TYPES.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setCustomerType(t)}
                  className={cn(
                    'rounded-lg border px-3 py-1.5 text-sm capitalize',
                    customerType === t ? 'border-brand bg-brand-subtle text-brand' : 'border-border',
                  )}
                >
                  {t}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <Button variant="secondary" className="flex-1" onClick={() => setStep(5)}>
                Skip
              </Button>
              <Button className="flex-1" loading={saving} onClick={() => void save({ step: 4, next: 5 })}>
                Continue
              </Button>
            </div>
          </div>
        )}

        {step === 5 && (
          <div className="mt-6 space-y-4">
            <p className="text-sm text-text-secondary">Select up to 12 services.</p>
            <div className="flex flex-wrap gap-2">
              {serviceSuggestions.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => toggleService(s)}
                  className={cn(
                    'rounded-lg border px-3 py-1.5 text-sm',
                    services.includes(s) ? 'border-brand bg-brand-subtle text-brand' : 'border-border',
                  )}
                >
                  {s}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                placeholder="Add custom service"
                value={customService}
                onChange={(e) => setCustomService(e.target.value)}
              />
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  if (customService.trim()) {
                    toggleService(customService.trim());
                    setCustomService('');
                  }
                }}
              >
                Add
              </Button>
            </div>
            <Input
              label="What makes you different (optional)"
              value={differentiator}
              onChange={(e) => setDifferentiator(e.target.value.slice(0, 200))}
              helper={`${differentiator.length}/200`}
            />
            <Input
              label="Years in business (optional)"
              inputMode="numeric"
              value={years}
              onChange={(e) => setYears(e.target.value.replace(/\D/g, '').slice(0, 3))}
            />
            <div className="flex gap-2">
              <Button variant="secondary" className="flex-1" onClick={() => setStep(6)}>
                Skip
              </Button>
              <Button className="flex-1" loading={saving} onClick={() => void save({ step: 5, next: 6 })}>
                Continue
              </Button>
            </div>
          </div>
        )}

        {step === 6 && (
          <div className="mt-6 space-y-4">
            <p className="text-sm text-text-secondary">
              You can start a website review now, or go to Home and pick it up later.
            </p>
            <Button
              className="w-full"
              size="lg"
              loading={saving}
              onClick={async () => {
                await save({ step: 6, complete: true });
                window.location.href = '/app/review';
              }}
            >
              Review my website
            </Button>
            <Button
              variant="secondary"
              className="w-full"
              size="lg"
              loading={saving}
              onClick={() => void save({ step: 6, complete: true })}
            >
              I&apos;ll do this later
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
